import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { canAssignRoles } from '@brickup/shared/constants'
import type { UserRole } from '@brickup/shared/enums'
import { authUserQuery } from '@/api/auth'
import { selectUserRoles, useAppStore } from '@/stores/use-app-store'

/**
 * Which accounts the signed-in roles run: those whose every role is one they
 * may hand out — so an admin runs every account that is not itself an
 * administrator, and root runs all of them.
 *
 * The same line the server draws through `UPDATE User`, so a screen does not
 * offer what a request is then refused for. What a screen does about the
 * reader's *own* account is its own question: deleting yourself is refused,
 * cutting the assistant you connected is not.
 *
 * Stable while the roles are: a table asks it per row from a `rowActions` that
 * `memo` compares by identity, and a fresh closure each render is a re-render
 * of every row.
 */
export function useRunsAccount(): (roles: readonly UserRole[]) => boolean {
  const viewerRoles = useAppStore(selectUserRoles)

  return useCallback(
    (roles: readonly UserRole[]) => canAssignRoles(viewerRoles ?? [], roles),
    [viewerRoles],
  )
}

/**
 * The signed-in account, for a screen that treats the reader's own row apart.
 *
 * Subscribed, so it answers with what is in the cache now: a one-off read
 * returns nothing on first mount, and every own-account test then matches no
 * row.
 */
export function useViewerAccount() {
  return useQuery(authUserQuery).data
}
